import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  FiHome,
  FiPackage,
  FiShoppingBag,
  FiUsers,
  FiFolder,
  FiTag,
  FiBarChart2,
  FiSettings,
  FiX,
  FiMenu,
  FiChevronLeft,
  FiChevronRight,
  FiLayout,
  FiTruck,
  FiChevronDown,
  FiChevronUp,
  FiMail,
  FiMessageSquare
} from 'react-icons/fi'

const AdminSidebar = ({ isOpen, onClose, onToggle, onCollapse }) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [shippingOpen, setShippingOpen] = useState(false)

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed
    setIsCollapsed(newCollapsedState)
    if (newCollapsedState) setShippingOpen(false)
    if (onCollapse) onCollapse(newCollapsedState)
  }

  const menuItems = [
    { path: '/admin/dashboard', icon: <FiHome />, label: 'Dashboard' },
    { path: '/admin/products', icon: <FiPackage />, label: 'Products' },
    { path: '/admin/orders', icon: <FiShoppingBag />, label: 'Orders' },
    { path: '/admin/users', icon: <FiUsers />, label: 'Users' },
    { path: '/admin/categories', icon: <FiFolder />, label: 'Categories' },
    { path: '/admin/coupons', icon: <FiTag />, label: 'Coupons' },
    { path: '/admin/reports', icon: <FiBarChart2 />, label: 'Reports' },
    { path: '/admin/homepage', icon: <FiLayout />, label: 'Homepage' },
    { path: '/admin/settings', icon: <FiSettings />, label: 'Settings' },
    { path: '/admin/newsletter', icon: <FiMail />, label: 'Newsletter' },
    { path: '/admin/contact', label: 'Messages', icon: <FiMessageSquare /> }
  ]

  const shippingSubItems = [
    { path: '/admin/shipping', label: 'Shipping Panel' },
    { path: '/admin/shipping/flash-express', label: 'Flash Express Settings' },
    { path: '/admin/shipping/jnt', label: 'J&T Express Settings' },
  ]

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white shadow-lg z-50
        transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'w-20' : 'w-64'}
      `}>
        <div className={`h-full flex flex-col relative ${isCollapsed ? 'w-20' : 'w-64'}`}>

          {/* Collapse arrow */}
          <div className="absolute -right-4 top-4 z-50">
            <button
              onClick={toggleCollapse}
              className="hidden lg:flex w-10 h-10 bg-[#ff6900] text-white rounded-full items-center justify-center hover:bg-[#e05d00] focus:outline-none shadow-lg transition-all hover:scale-110"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? <FiChevronRight size={22} /> : <FiChevronLeft size={22} />}
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            {!isCollapsed ? (
              <>
                <h2 className="text-xl font-bold text-[#ff6900] truncate">Admin Panel</h2>
                <div className="flex items-center gap-2">
                  <button onClick={onClose} className="lg:hidden p-1 hover:bg-gray-100 rounded">
                    <FiX size={20} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-primary-600 w-full text-center">AP</h2>
                <button onClick={onClose} className="lg:hidden p-1 hover:bg-gray-100 rounded absolute right-2">
                  <FiX size={20} />
                </button>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">

              {/* Regular menu items */}
              {menuItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => { if (window.innerWidth < 1024) onClose() }}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-3 rounded-lg transition-colors group relative ${isActive ? 'bg-[#ff6900] text-white' : 'text-gray-700 hover:bg-orange-50'
                      } ${isCollapsed ? 'justify-center' : ''}`
                    }
                    title={isCollapsed ? item.label : ''}
                  >
                    <span className={`text-xl ${!isCollapsed && 'mr-3'}`}>{item.icon}</span>
                    {!isCollapsed && item.label}
                    {isCollapsed && (
                      <span className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                        {item.label}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}

              {/* Shipping — expandable group */}
              <li>
                {isCollapsed ? (
                  // Collapsed: just show icon linking to /admin/shipping
                  <NavLink
                    to="/admin/shipping"
                    onClick={() => { if (window.innerWidth < 1024) onClose() }}
                    className={({ isActive }) =>
                      `flex items-center justify-center px-4 py-3 rounded-lg transition-colors group relative ${isActive ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                    title="Shipping"
                  >
                    <span className="text-xl"><FiTruck /></span>
                    <span className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                      Shipping
                    </span>
                  </NavLink>
                ) : (
                  <>
                    {/* Accordion toggle */}
                    <button
                      onClick={() => setShippingOpen(o => !o)}
                      className="w-full flex items-center px-4 py-3 rounded-lg transition-colors text-gray-700 hover:bg-gray-100"
                    >
                      <span className="text-xl mr-3"><FiTruck /></span>
                      <span className="flex-1 text-left">Shipping</span>
                      {shippingOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                    </button>

                    {/* Sub-items */}
                    {shippingOpen && (
                      <ul className="mt-1 ml-4 pl-4 border-l border-gray-200 space-y-1">
                        {shippingSubItems.map((sub) => (
                          <li key={sub.path}>
                            <NavLink
                              to={sub.path}
                              end
                              onClick={() => { if (window.innerWidth < 1024) onClose() }}
                              className={({ isActive }) =>
                                `block px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                                }`
                              }
                            >
                              {sub.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>

            </ul>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t">
            {!isCollapsed ? (
              <div className="text-sm text-gray-600 text-center">
                <p>Version 1.0.0</p>
                <p>&copy; {new Date().getFullYear()} E-Store</p>
              </div>
            ) : (
              <div className="text-xs text-gray-600 text-center">
                <p>v1.0</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

export default AdminSidebar